// services/wrapped/audioEngine.ts
// Trilha + efeitos do LibriTui Wrapped, sincronizados com as cenas do renderer.
// Os áudios vêm do R2 (assets/wrapped/, gerados por scripts/wrapped/build-audio.mjs);
// se o R2 falhar, o mesmo sintetizador gera tudo no navegador.
import { renderMusic, renderSfx, SfxName, MusicStyle, StereoBuffer } from './synth';
import { SFX_CUES } from './renderer';

export const WRAPPED_ASSETS_BASE = 'https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/assets/wrapped';
const SFX_NAMES: SfxName[] = ['whoosh', 'pop', 'tick', 'chime', 'rise', 'sparkle'];
const MUSIC_GAIN = 0.55;

export interface WrappedAudioBuffers {
  music: Partial<Record<MusicStyle, AudioBuffer>>;
  sfx: Partial<Record<SfxName, AudioBuffer>>;
}

const toAudioBuffer = (ctx: BaseAudioContext, sb: StereoBuffer) => {
  const b = ctx.createBuffer(2, sb.left.length, sb.sampleRate);
  b.copyToChannel(sb.left as Float32Array<ArrayBuffer>, 0);
  b.copyToChannel(sb.right as Float32Array<ArrayBuffer>, 1);
  return b;
};

const fetchBuffer = async (ctx: BaseAudioContext, file: string): Promise<AudioBuffer | null> => {
  try {
    const res = await fetch(`${WRAPPED_ASSETS_BASE}/${file}`, { mode: 'cors' });
    if (!res.ok) return null;
    return await ctx.decodeAudioData(await res.arrayBuffer());
  } catch {
    return null;
  }
};

/** Carrega a trilha escolhida e os efeitos (R2 primeiro, síntese local como reserva). */
export const loadWrappedAudio = async (ctx: BaseAudioContext, style: MusicStyle, into?: WrappedAudioBuffers): Promise<WrappedAudioBuffers> => {
  const out: WrappedAudioBuffers = into || { music: {}, sfx: {} };
  const tasks: Promise<void>[] = [];
  if (!out.music[style]) {
    tasks.push(fetchBuffer(ctx, `music-${style}.wav`).then(b => { out.music[style] = b || toAudioBuffer(ctx, renderMusic(style)); }));
  }
  for (const n of SFX_NAMES) {
    if (out.sfx[n]) continue;
    tasks.push(fetchBuffer(ctx, `sfx-${n}.wav`).then(b => { out.sfx[n] = b || toAudioBuffer(ctx, renderSfx(n)); }));
  }
  await Promise.all(tasks);
  return out;
};

/**
 * Toca a trilha a partir de um instante qualquer (play/pausa/salto de cena) e agenda os efeitos.
 * Com `toStream`, a saída vai só para um MediaStream (usado na exportação do vídeo, em silêncio).
 */
export class WrappedAudioPlayer {
  ctx: AudioContext;
  master: GainNode;
  stream: MediaStreamAudioDestinationNode | null = null;
  buffers: WrappedAudioBuffers;
  style: MusicStyle;
  sources: AudioScheduledSourceNode[] = [];
  startedAt = 0;
  offset = 0;
  playing = false;

  constructor(buffers: WrappedAudioBuffers, style: MusicStyle, toStream = false) {
    this.ctx = new AudioContext();
    this.master = this.ctx.createGain();
    this.buffers = buffers;
    this.style = style;
    if (toStream) {
      this.stream = this.ctx.createMediaStreamDestination();
      this.master.connect(this.stream);
    } else {
      this.master.connect(this.ctx.destination);
    }
  }

  /** Tempo atual do vídeo, guiado pelo relógio do áudio (mantém imagem e som juntos). */
  time() {
    return this.playing ? this.offset + (this.ctx.currentTime - this.startedAt) : this.offset;
  }

  async play(from: number) {
    this.stopSources();
    if (this.ctx.state === 'suspended') await this.ctx.resume().catch(() => {});
    const now = this.ctx.currentTime + 0.05;
    this.startedAt = now;
    this.offset = from;
    this.playing = true;

    const music = this.buffers.music[this.style];
    if (music && from < music.duration) {
      const src = this.ctx.createBufferSource();
      src.buffer = music;
      const g = this.ctx.createGain();
      g.gain.value = MUSIC_GAIN;
      src.connect(g).connect(this.master);
      src.start(now, from);
      this.sources.push(src);
    }
    for (const cue of SFX_CUES) {
      if (cue.t < from - 0.02) continue;
      const buf = this.buffers.sfx[cue.name];
      if (!buf) continue;
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      const g = this.ctx.createGain();
      g.gain.value = cue.gain;
      src.connect(g).connect(this.master);
      src.start(now + Math.max(0, cue.t - from));
      this.sources.push(src);
    }
  }

  pause() {
    if (!this.playing) return;
    this.offset = this.time();
    this.playing = false;
    this.stopSources();
  }

  seek(t: number) {
    if (this.playing) this.play(t);
    else this.offset = t;
  }

  setStyle(style: MusicStyle) {
    this.style = style;
    if (this.playing) this.play(this.time());
  }

  setMuted(m: boolean) {
    this.master.gain.setTargetAtTime(m ? 0 : 1, this.ctx.currentTime, 0.02);
  }

  stopSources() {
    for (const s of this.sources) { try { s.stop(); } catch { /* já parado */ } }
    this.sources = [];
  }

  dispose() {
    this.stopSources();
    this.playing = false;
    this.ctx.close().catch(() => {});
  }
}
